/**
 * Prev/next navigation for the product gallery AND the full-screen modal.
 *
 * Two Dawn behaviours this corrects, both additively — no Dawn file is
 * modified, and both drive Dawn's own elements rather than replacing them.
 *
 * 1. INLINE GALLERY. Dawn ships slider buttons but hides them above 750px
 *    (`.slider--mobile + .slider-buttons { display:none }`), because the
 *    thumbnail layouts swap the main image instead of scrolling it. So on
 *    desktop there is no way to page the main image. We add arrows that call
 *    Dawn's public setActiveMedia() / setActiveThumbnail().
 *
 * 2. FULL-SCREEN MODAL. product-modal.js renders EVERY media item into one
 *    scrollable container, marks one `.active` and scrollIntoView()s it — so
 *    opening full screen gives a giant scrolling stack of every image. We add
 *    arrows that move the `.active` class one item at a time; the paired CSS
 *    (hofsm.css §16) hides everything that is not `.active`.
 */
(function () {
  const ARROW = (dir) =>
    '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">' +
    '<path d="' + (dir === 'prev' ? 'M15 4 L7 12 L15 20' : 'M9 4 L17 12 L9 20') +
    '" fill="none" stroke="currentColor" stroke-width="2.2" ' +
    'stroke-linecap="round" stroke-linejoin="round"/></svg>';

  function button(dir, label, cls) {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = cls + ' ' + cls + '--' + dir;
    b.setAttribute('aria-label', label);
    b.innerHTML = ARROW(dir);
    return b;
  }

  /* ---------------- inline gallery ---------------- */
  function initGallery(gallery) {
    if (gallery.dataset.hofsmArrows === 'on') return;
    const viewer = gallery.querySelector('[id^="GalleryViewer"]');
    const thumbs = gallery.querySelector('[id^="GalleryThumbnails"]');
    if (!viewer || !thumbs) return;

    const ids = Array.from(thumbs.querySelectorAll('[data-target]')).map((el) => el.dataset.target);
    if (ids.length < 2) return;

    const wrap = viewer.querySelector('.product__media-list')?.parentElement || viewer;
    if (getComputedStyle(wrap).position === 'static') wrap.style.position = 'relative';

    const go = (step) => {
      const active = viewer.querySelector('[data-media-id].is-active');
      let i = active ? ids.indexOf(active.dataset.mediaId) : -1;
      if (i < 0) i = 0;
      const next = ids[(i + step + ids.length) % ids.length];
      if (typeof gallery.setActiveMedia === 'function') gallery.setActiveMedia(next, false);
      if (typeof gallery.setActiveThumbnail === 'function') {
        gallery.setActiveThumbnail(thumbs.querySelector('[data-target="' + next + '"]'));
      }
    };

    const prev = button('prev', 'Previous image', 'hofsm-gallery-arrow');
    const next = button('next', 'Next image', 'hofsm-gallery-arrow');
    prev.addEventListener('click', () => go(-1));
    next.addEventListener('click', () => go(1));
    wrap.appendChild(prev);
    wrap.appendChild(next);
    wrap.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') { e.preventDefault(); go(-1); }
      if (e.key === 'ArrowRight') { e.preventDefault(); go(1); }
    });
    gallery.dataset.hofsmArrows = 'on';
  }

  /* ---------------- full-screen modal ---------------- */
  function initModal(modal) {
    if (modal.dataset.hofsmArrows === 'on') return;
    const content = modal.querySelector('[role="document"]');
    if (!content) return;

    const items = () => Array.from(modal.querySelectorAll('[data-media-id]'));
    if (items().length < 2) return;

    const counter = document.createElement('div');
    counter.className = 'hofsm-modal-counter';

    const setActive = (idx) => {
      const list = items();
      const i = (idx + list.length) % list.length;
      list.forEach((el, n) => el.classList.toggle('active', n === i));
      counter.textContent = i + 1 + ' / ' + list.length;
      content.scrollTop = 0;
      content.scrollLeft = 0;
    };

    const go = (step) => {
      const list = items();
      const cur = list.findIndex((el) => el.classList.contains('active'));
      setActive((cur < 0 ? 0 : cur) + step);
    };

    const prev = button('prev', 'Previous image', 'hofsm-modal-arrow');
    const next = button('next', 'Next image', 'hofsm-modal-arrow');
    prev.addEventListener('click', (e) => { e.stopPropagation(); go(-1); });
    next.addEventListener('click', (e) => { e.stopPropagation(); go(1); });

    const dialog = modal.querySelector('.product-media-modal__dialog') || modal;
    dialog.appendChild(prev);
    dialog.appendChild(next);
    dialog.appendChild(counter);

    modal.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') { e.preventDefault(); go(-1); }
      if (e.key === 'ArrowRight') { e.preventDefault(); go(1); }
    });

    // Dawn sets `.active` in showActiveMedia() when the modal opens; pick the
    // counter up from whatever it chose, after its work has run.
    const sync = () => {
      const list = items();
      const cur = list.findIndex((el) => el.classList.contains('active'));
      setActive(cur < 0 ? 0 : cur);
    };
    const origShow = modal.show?.bind(modal);
    if (origShow) {
      modal.show = function (opener) { origShow(opener); requestAnimationFrame(sync); };
    }

    modal.dataset.hofsmArrows = 'on';
  }

  const boot = () => {
    document.querySelectorAll('media-gallery').forEach(initGallery);
    document.querySelectorAll('product-modal').forEach(initModal);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();
